import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { X, Users, Mail, Check, ChevronDown, ChevronRight, User, Loader2, CheckCircle, Edit3 } from 'lucide-react';
import { GET_RECIPIENTS_PREVIEW_QUERY } from '../../services/graphql/queries.js';
import {
  generateCIFDescription,
  getBadgeColor
} from '../../utils/recipientsModalHelpers.js';

const ApprovalModal = ({ isOpen, onClose, event, onConfirm, user }) => {
  const [statusDetail, setStatusDetail] = useState('');
  const [recipients, setRecipients] = useState({
    byConsignee: []
  });
  const [selectedEmailsBySection, setSelectedEmailsBySection] = useState({
    consignee: {}
  });
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  // Query for existing shipments
  const { data, loading, error } = useQuery(GET_RECIPIENTS_PREVIEW_QUERY, {
    variables: { manifest: event?.shipment?.manifest },
    skip: !isOpen || !event?.shipment?.manifest,
    fetchPolicy: 'network-only'
  });

  // Initialize status detail when event changes
  useEffect(() => {
    if (event) {
      setStatusDetail(event.status_detail || '');
    }
  }, [event]);

  // Initialize recipients data and selected emails
  useEffect(() => {
    const recipientsData = data?.getRecipientsPreview;
    if (recipientsData) {
      setRecipients(recipientsData);
      
      // Initialize consignee groups (empty - user must select manually)
      const newSelectedBySection = { consignee: {} };
      recipientsData.byConsignee.forEach((group, groupIndex) => {
        newSelectedBySection.consignee[groupIndex] = new Set();
      });
      setSelectedEmailsBySection(newSelectedBySection);
      
      // Expand ALL consignee sections by default
      const defaultExpanded = new Set();
      recipientsData.byConsignee.forEach((_, index) => {
        defaultExpanded.add(`consignee-${index}`);
      });
      setExpandedSections(defaultExpanded);
    }
  }, [data]);

  // Toggle email selection for consignee section only
  const handleToggleEmailSelection = (email, sectionIndex) => {
    const newSelectedBySection = { consignee: { ...selectedEmailsBySection.consignee } };
    
    if (!newSelectedBySection.consignee[sectionIndex]) {
      newSelectedBySection.consignee[sectionIndex] = new Set();
    }
    const consigneeSet = new Set(newSelectedBySection.consignee[sectionIndex]);
    
    if (consigneeSet.has(email)) {
      consigneeSet.delete(email);
    } else {
      consigneeSet.add(email);
    }
    
    newSelectedBySection.consignee[sectionIndex] = consigneeSet;
    setSelectedEmailsBySection(newSelectedBySection);
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Select/Deselect all emails in a consignee section
  const handleToggleSectionEmails = (emails, selectAll, sectionIndex) => {
    const newSelectedBySection = { consignee: { ...selectedEmailsBySection.consignee } };
    
    if (!newSelectedBySection.consignee[sectionIndex]) {
      newSelectedBySection.consignee[sectionIndex] = new Set();
    }
    const consigneeSet = new Set(newSelectedBySection.consignee[sectionIndex]);
    
    if (selectAll) {
      emails.forEach(email => consigneeSet.add(email));
    } else {
      emails.forEach(email => consigneeSet.delete(email));
    }
    
    newSelectedBySection.consignee[sectionIndex] = consigneeSet;
    setSelectedEmailsBySection(newSelectedBySection);
  };

  // Handle confirm - Return granular structure by group/line
  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      // Build granular structure: groupIndex → selectedEmails
      const selectedRecipientsByGroup = [];
      
      // Process each consignee group (each group = one Shipment_Details line)
      Object.entries(selectedEmailsBySection.consignee).forEach(([groupIndex, emailsSet]) => {
        if (emailsSet && emailsSet.size > 0) {
          const groupData = recipients.byConsignee[parseInt(groupIndex)];
          if (groupData) {
            selectedRecipientsByGroup.push({
              groupIndex: parseInt(groupIndex),
              consigneeName: groupData.consigneeName,
              bill_of_lading: groupData.bill_of_lading,
              selectedEmails: Array.from(emailsSet)
            });
          }
        }
      });
      
      await onConfirm({
        eventId: event.id,
        statusDetail: statusDetail.trim(),
        selectedRecipientsByGroup
      });
    } catch (error) {
      console.error('Error processing approval:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    if (!isProcessing) {
      setStatusDetail('');
      setSelectedEmailsBySection({
        consignee: {}
      });
      setExpandedSections(new Set());
      setIsProcessing(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Calculate total selected emails
  const totalSelectedEmails = (() => {
    const allSelectedEmails = new Set();
    Object.values(selectedEmailsBySection.consignee).forEach(consigneeSet => {
      if (consigneeSet) {
        consigneeSet.forEach(email => allSelectedEmails.add(email));
      }
    });
    return allSelectedEmails.size;
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                Aprobar Estado del Envío
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Modifica el estado si es necesario y selecciona los destinatarios para notificación por email
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isProcessing ? (
            <div className="text-center py-8">
              <Loader2 className="animate-spin h-8 w-8 text-green-600 mx-auto" />
              <p className="text-gray-600 mt-2 font-medium">Aprobando estado y enviando correos...</p>
              <p className="text-gray-500 text-sm mt-1">Este proceso puede tardar unos momentos</p>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Cargando destinatarios...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Error al cargar destinatarios: {error.message}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Shipment Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Información del Envío</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Manifiesto:</span>
                    <span className="ml-2 font-medium">{event?.shipment?.manifest}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Referencia:</span>
                    <span className="ml-2 font-medium">{event?.shipment?.reference}</span>
                  </div>
                </div>
              </div>

              {/* Status Detail Editor */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Estado del Envío
                </h4>
                <textarea
                  value={statusDetail}
                  onChange={(e) => setStatusDetail(e.target.value)}
                  placeholder="Descripción del estado del envío..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
                <p className="text-xs text-blue-700 mt-1">
                  Puedes modificar la descripción del estado antes de aprobar
                </p>
              </div>

              {/* Recipients by Consignee */}
              {recipients.byConsignee.map((consigneeGroup, groupIndex) => (
                <div key={groupIndex} className="border border-gray-200 rounded-lg">
                  <div className="bg-green-50 p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleSection(`consignee-${groupIndex}`)}
                        className="flex items-center space-x-2 text-green-900 font-medium"
                      >
                        {expandedSections.has(`consignee-${groupIndex}`) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <Mail className="h-4 w-4" />
                        <span>
                          {consigneeGroup.consigneeName} - BL: {consigneeGroup.bill_of_lading} ({consigneeGroup.recipients.length})
                        </span>
                      </button>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleSectionEmails(consigneeGroup.recipients.map(r => r.email), true, groupIndex)}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Seleccionar todos
                        </button>
                        <button
                          onClick={() => handleToggleSectionEmails(consigneeGroup.recipients.map(r => r.email), false, groupIndex)}
                          className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Deseleccionar todos
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      {generateCIFDescription(consigneeGroup)}
                    </p>
                  </div>
                  
                  {expandedSections.has(`consignee-${groupIndex}`) && (
                    <div className="p-4 space-y-2">
                      {consigneeGroup.recipients.map((recipient, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded ml-4">
                          <button
                            onClick={() => handleToggleEmailSelection(recipient.email, groupIndex)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selectedEmailsBySection.consignee[groupIndex]?.has(recipient.email)
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 hover:border-green-400'
                            }`}
                          >
                            {selectedEmailsBySection.consignee[groupIndex]?.has(recipient.email) && <Check className="h-3 w-3" />}
                          </button>
                          <User className="h-4 w-4 text-gray-400" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{recipient.name}</div>
                            <div className="text-xs text-gray-500">{recipient.email}</div>
                          </div>
                          <div className={`text-xs px-2 py-1 rounded ${getBadgeColor(recipient.type)}`}>
                            {recipient.type}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Summary */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-900 mb-2">Resumen</h4>
                <p className="text-sm text-yellow-800">
                  <strong>{totalSelectedEmails}</strong> destinatario(s) seleccionado(s) recibirán la notificación por email.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={totalSelectedEmails === 0 || !statusDetail.trim() || isProcessing}
              className="flex items-center px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <Mail className="h-4 w-4 mr-1" />
                  Aprobar y enviar correos ({totalSelectedEmails})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;